import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from '../../shared/auth/AuthContext';
import { apiClient } from '../../shared/api/client';
import { queryKeys } from '../../shared/api/query-keys';
import {
  Button,
  Card,
  Input,
  Select,
} from '@nexora/design-system';

interface ProductFormData {
  type: string;
  name: string;
  description: string;
  sku: string;
  barcode: string;
  unit: string;
  ncmCode: string;
  cestCode: string;
  price: number;
  costPrice: number;
}

const initialForm: ProductFormData = {
  type: 'PRODUCT',
  name: '',
  description: '',
  sku: '',
  barcode: '',
  unit: 'UN',
  ncmCode: '',
  cestCode: '',
  price: 0,
  costPrice: 0,
};

export function ProductFormPage(): JSX.Element {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<ProductFormData>(initialForm);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const createMutation = useMutation({
    mutationFn: async (data: ProductFormData) =>
      apiClient.post('/products', { ...data, tenantId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.masterData.products(tenantId) });
      navigate({ to: '/master-data/products' });
    },
    onError: (err: Error) => {
      setErrors({ submit: err.message });
    },
  });

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!form.sku) newErrors['sku'] = 'SKU é obrigatório';
    if (!form.name) newErrors['name'] = 'Nome é obrigatório';
    if (form.price <= 0) newErrors['price'] = 'Preço de venda deve ser > 0';
    if (form.costPrice <= 0) newErrors['costPrice'] = 'Preço de custo deve ser > 0';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      createMutation.mutate(form);
    }
  };

  const updateField = <K extends keyof ProductFormData>(
    field: K,
    value: ProductFormData[K],
  ) => {
    setForm({ ...form, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  return (
    <div className="product-form-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
                  <h1 className="font-display-lg text-display-lg text-on-surface m-0">Novo Produto</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Cadastro de produto, tributação e estoque.</p>
        </div>
        <div className="flex flex-wrap items-center gap-sm">
          <Button variant="ghost" onClick={() => navigate({ to: '/master-data/products' })}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </div>

      <Card>
        <div className="form-grid">
          <div className="form-row">
            <Select
              label="Tipo *"
              value={form.type}
              onChange={(e) => updateField('type', e.target.value)}
            >
              <option value="PRODUCT">Produto</option>
              <option value="SERVICE">Serviço</option>
              <option value="COMPOSITE">Composto</option>
              <option value="CONSUMABLE">Consumível</option>
              <option value="TOOL">Ferramenta</option>
            </Select>
            <Input
              label="SKU *"
              value={form.sku}
              onChange={(e) => updateField('sku', e.target.value)}
              error={errors['sku']}
              placeholder="Código SKU do produto"
            />
          </div>
          <Input
            label="Código de Barras"
            value={form.barcode}
            onChange={(e) => updateField('barcode', e.target.value)}
            placeholder="Código de barras (EAN)"
          />
          <Input
            label="Nome *"
            value={form.name}
            onChange={(e) => updateField('name', e.target.value)}
            error={errors['name']}
            placeholder="Nome do produto"
          />
          <Input
            label="Descrição"
            value={form.description}
            onChange={(e) => updateField('description', e.target.value)}
          />
          <div className="form-row">
            <Select
              label="Unidade"
              value={form.unit}
              onChange={(e) => updateField('unit', e.target.value)}
            >
              <option value="UN">Unidade (UN)</option>
              <option value="KG">Quilograma (KG)</option>
              <option value="PC">Peça (PC)</option>
              <option value="CX">Caixa (CX)</option>
              <option value="LT">Litro (LT)</option>
              <option value="M">Metro (M)</option>
              <option value="M2">Metro² (M2)</option>
              <option value="M3">Metro³ (M3)</option>
            </Select>
            <Input
              label="NCM"
              value={form.ncmCode}
              onChange={(e) => updateField('ncmCode', e.target.value)}
              placeholder="Ex: 8471.30.11"
            />
            <Input
              label="CEST"
              value={form.cestCode}
              onChange={(e) => updateField('cestCode', e.target.value)}
              placeholder="Ex: 17.003.00"
            />
          </div>
          <div className="form-row">
            <Input
              label="Preço de Venda *"
              type="number"
              step="0.01"
              value={String(form.price)}
              onChange={(e) => updateField('price', Number(e.target.value))}
              error={errors['price']}
            />
            <Input
              label="Preço de Custo *"
              type="number"
              step="0.01"
              value={String(form.costPrice)}
              onChange={(e) => updateField('costPrice', Number(e.target.value))}
              error={errors['costPrice']}
            />
          </div>
        </div>

        {errors['submit'] && (
          <p className="error-message">{errors['submit']}</p>
        )}
      </Card>

      <style>{`
        .product-form-page { padding: 1.5rem; }
        .header-actions { display: flex; gap: 0.5rem; }
        /* O formulario ocupava 640px dentro de um card de ~1200px: metade do
           card ficava vazia. A referencia usa grid de 2 colunas nos cadastros
           (grid-cols-1 md:grid-cols-2, 55+20 ocorrencias nas 128 telas).
           auto-fit preserva o agrupamento em linhas do autor: linha de um
           campo ocupa a largura, de dois divide, de tres reparte em tres. */
        .form-grid { display: flex; flex-direction: column; gap: 1rem; max-width: 1040px; }
        .form-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 1rem; align-items: start; }
        .error-message { color: #ba1a1a; font-size: 0.875rem; margin-top: 1rem; }
      `}</style>
    </div>
  );
}
